/* GASSISA
=========================================================================================
* Descripción: 
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
 *      1       | 23/11/2023 | Lic. José Luis Aragón Cervantes  | Creación
 *      2       | 11/06/2026 | Lic. José Luis Aragón Cervantes  | Navegación por flechas arriba/abajo con scrollIntoView
* ---------------------------------------------------------------------------------------
*/

import TextField from "../ui/TextField";
import { Button, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography, Paper } from "@mui/material";

import { Fragment, useEffect, useMemo, useState } from "react";
import { useLayout } from "../../context/System/LayoutContext";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import PropTypes from "prop-types";
import getObjectValue from "lodash.get";

export default function SizeSoftXTableVentas(props) {
    const {
        data,
        columns,
        id,
        row,
        setRow,
        defaultRow,
        disableStickyHeader,
        SubRow,
        reiniciarTabla,
        fontSize = "medium",
        rowSpacing = 1,
        rowsPerPageIntro = 6,
        showFilters = false,
        fnDoubleClick,
        disablePagination = false,
        onFilteredDataChange,
        onFiltersChange,
        onSortChange,
     } = props;

    const safeData = Array.isArray(data) ? data : [];

    const [activeIndex, setActiveIndex] = useState(0);

    const pager = [3, 4, 5, 6, 9, 10, 15, 20, 50, 100];
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(pager[0]);
    const [dataRows, setDataRows] = useState(safeData);
    const [orderBy, setOrderBy] = useState("");
    const [order, setOrder] = useState(true);
    const { tablaReferencia } = useLayout();

    const [columnFilters, setColumnFilters] = useState({});

    // ðŸ” Filtramos sobre dataRows, NO sobre data
    const filteredRows = useMemo(() =>
         dataRows.filter((row) =>
             columns.every((column) => {
                 const filterValue = columnFilters[column.field];
                 if (!filterValue) return true;
                 const cellValue =
                     getObjectValue(row, column.field)?.toString().toLowerCase() || "";
                 return cellValue.includes(filterValue.toLowerCase());
             })
         ), [dataRows, columns, columnFilters]
     );

    useEffect(() => {
         if (!onFilteredDataChange) return;
         if (Object.values(columnFilters).some((v) => v)) {
             onFilteredDataChange(filteredRows);
         } else {
             onFilteredDataChange([]);
         }
     }, [columnFilters, filteredRows, onFilteredDataChange]);

     useEffect(() => {
         if (onFiltersChange) {
             onFiltersChange(columnFilters);
         }
     }, [columnFilters, onFiltersChange]);

    const paginatedRows = disablePagination
        ? filteredRows 
        : filteredRows.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

    const getMaxWidthForColumn = (field) => {
        if (!Array.isArray(dataRows)) return 100;
        const maxDataWidth = dataRows.reduce((maxWidth, row) => {
            const cellValue = getObjectValue(row, field) || "";
            return Math.max(maxWidth, cellValue.toString().length * 6);
        }, 0);
        return maxDataWidth > 100 ? maxDataWidth : 100;
    };

    const handleChangePage = (e, page) => {
        setPage(page);
    };

    const handleChangeRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
    };

    const handleClickOrder = (field) => {
        const isAsc = orderBy === field && order;
        const newOrder = !isAsc;

        const sortedData = ordenarFilas(dataRows, field, newOrder);
        setDataRows(sortedData);
        setOrder(newOrder);
        setOrderBy(field);
        if (onSortChange) onSortChange(sortedData);
    };

    // Mismo comparador en un solo lugar para no divergir
    const ordenarFilas = (filas, campo, ascendente) => {
        if (!campo) return filas;
        return [...filas].sort((a, b) => {
            let valueA = a[campo] ?? "";
            let valueB = b[campo] ?? "";

            if (!isNaN(valueA) && !isNaN(valueB)) {
                return ascendente ? valueA - valueB : valueB - valueA;
            }

            if (typeof valueA === "string" && typeof valueB === "string") {
                return ascendente
                    ? valueA.localeCompare(valueB)
                    : valueB.localeCompare(valueA);
            }

            return 0;
        });
    };

    const handleClickRow = (valueRow) => () => {
        if (!defaultRow) {
            setRow(valueRow);
            return;
        }
        setRow(valueRow[id] === row[id] ? defaultRow : valueRow);
    };

    const scrollRowIntoView = (el) => {
        const container = el.closest('.MuiTableContainer-root');
        if (!container) return;
        const thead = container.querySelector('thead');
        const headerH = thead?.offsetHeight || 40;
        const cr = container.getBoundingClientRect();
        const rr = el.getBoundingClientRect();
        const relTop = rr.top - cr.top;
        const relBottom = rr.bottom - cr.top;
        if (relBottom > cr.height) {
            container.scrollTop += relBottom - cr.height + 4;
        } else if (relTop < headerH) {
            container.scrollTop -= headerH - relTop + 4;
        }
    };

    const handleRowKeyDown = (e, rowData, index) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            e.stopPropagation();
            const nextIdx = index + 1 < filteredRows.length ? index + 1 : 0;
            setRow(filteredRows[nextIdx]);
            const nextEl = document.querySelector(`#row-${nextIdx}`);
            if (nextEl) {
                nextEl.focus();
                scrollRowIntoView(nextEl);
            }
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            e.stopPropagation();
            const prevIdx = index - 1 >= 0 ? index - 1 : filteredRows.length - 1;
            setRow(filteredRows[prevIdx]);
            const prevEl = document.querySelector(`#row-${prevIdx}`);
            if (prevEl) {
                prevEl.focus();
                scrollRowIntoView(prevEl);
            }
        }
    };

    // ðŸ” Cuando cambia data, actualizamos dataRows de forma segura
    useEffect(() => {
        const newData = Array.isArray(data) ? data : [];
        const ordenada = ordenarFilas(newData, orderBy, order);
        setDataRows(ordenada);
        if (onSortChange) onSortChange(ordenada);
        // setPage(0);
    }, [data, orderBy, order]);

    useEffect(() => {
        setRowsPerPage(rowsPerPageIntro);
        setPage(0);
    }, [rowsPerPageIntro]);

    // Enfocar fila cuando cambia
    useEffect(() => {
        const rowEl = document.querySelector(`#row-${activeIndex}`);
        if (rowEl) rowEl.focus();
    }, [activeIndex]);

    return (
        <Fragment>
            <TableContainer component={Paper}>
                <Table
                    size="small"
                    ref={tablaReferencia}
                    stickyHeader={!disableStickyHeader}
                    className="sizesoftx-table"
                >
                    <TableHead>
                        <TableRow>
                            {SubRow && <TableCell></TableCell>}
                            {columns.map((column, i) => (
                                <TableCell
                                    key={column.field || i}
                                    style={{
                                        minWidth: column.width || getMaxWidthForColumn(column.field),
                                        width: column.width || undefined,
                                        height: "100px",
                                        whiteSpace: "nowrap",
                                        fontSize,
                                        padding: "3px 6px",
                                    }}
                                >
                                    <TextField autoComplete="off"
                                        variant="standard"
                                        size="small"
                                        placeholder="Filtrar"
                                        value={columnFilters[column.field] || ""}
                                        onChange={(e) =>
                                            setColumnFilters((prev) => ({
                                                ...prev,
                                                [column.field]: e.target.value,
                                            }))
                                        }
                                        style={{
                                            display: showFilters ? "block" : "none",
                                            width: "100%",
                                            fontSize:
                                                fontSize === "small"
                                                    ? "0.75rem"
                                                    : "0.875rem",
                                            marginTop: 4,
                                        }}
                                    />
                                    {!column.hideSort && (
                                        <Button
                                            style={{
                                                textOverflow: "ellipsis",
                                                overflow: "hidden",
                                                whiteSpace: "nowrap",
                                                textTransform: "none",
                                                fontSize,
                                            }}
                                            variant="text"
                                            color="inherit"
                                            onClick={() => handleClickOrder(column.field)}
                                            startIcon={
                                                orderBy === column.field
                                                    ? order
                                                        ? <ArrowUpwardIcon />
                                                        : <ArrowDownwardIcon />
                                                    : null
                                            }
                                        >
                                            <Typography
                                                color="secondary"
                                                variant="button"
                                                sx={{ fontWeight: "600" }}
                                                style={{
                                                    fontSize,
                                                    margin: "none",
                                                    padding: "none",
                                                }}
                                            >
                                                {column.title}
                                            </Typography>
                                        </Button>
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredRows.length > 0 ? (
                            paginatedRows.map((dataRow, i) => (
                                <TableRow
                                    key={i}
                                    id={`row-${i}`}
                                    tabIndex={-1}
                                    onClick={handleClickRow(dataRow)}
                                    onDoubleClick={() => fnDoubleClick?.(dataRow)}
                                    onKeyDown={(e) => handleRowKeyDown(e, dataRow, i)}
                                    className={
                                        row && dataRow[id] === row[id] ? "row" : "no-row"
                                    }
                                    style={{ height: 6 + rowSpacing, fontSize }}
                                >
                                    {columns.map((column, j) => (
                                        <TableCell
                                            key={j}
                                            align={column.align || "left"}
                                            style={{
                                                fontSize,
                                                padding: "1px 8px",
                                                position: "relative",
                                            }}
                                        >
                                            {column.render
                                                ? column.render(dataRow, i)
                                                : (
                                                    <Typography>
                                                        {getObjectValue(
                                                            dataRow,
                                                            column.field
                                                        ) ?? ""}
                                                    </Typography>
                                                )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} align="center">
                                    <Typography>
                                        No se encontraron registros para mostrar
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                {!disablePagination && (
                    <TablePagination
                        count={filteredRows.length}
                        component="div"
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={pager}
                        labelRowsPerPage="Registros por pégina"
                    />
                )}
            </TableContainer>
        </Fragment>
    );
}

SizeSoftXTableVentas.propTypes = {
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            field: PropTypes.string,
            title: PropTypes.string,
            render: PropTypes.func,
            hideSort: PropTypes.bool,
            width: PropTypes.number,
        })
    ).isRequired,
    data: PropTypes.array, // ya no lo marcamos como required tan rígido
    defaultRow: PropTypes.object,
    id: PropTypes.string.isRequired,
    fontSize: PropTypes.oneOf(["small", "medium", "large"]),
    row: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.oneOf([null])]),
    setRow: PropTypes.func.isRequired,
    disableStickyHeader: PropTypes.bool,
};
